import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, 
  Mic, 
  Film, 
  Mail, 
  Moon, 
  Sparkles, 
  Target,
  TrendingUp,
  Calendar,
  Download,
  Search,
  Filter,
  Lock,
  Heart,
  Brain,
  Activity,
  Zap,
  Eye,
  ArrowLeft,
  ChevronLeft,
  HeartPulse,
  Users
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { PageHeader } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { useEditableContent } from '@/hooks/useEditableContent';
import { CorrelationExplorer as CorrelationExplorerComponent } from '@/components/insights/CorrelationExplorer';
import { EmotionalPatternsExplorer } from '@/components/insights/EmotionalPatternsExplorer';
import { EnergeticPatternsExplorer } from '@/components/insights/EnergeticPatternsExplorer';
import { MentalPatternsExplorer } from '@/components/insights/MentalPatternsExplorer';
import { RelationshipPulseChecker } from '@/components/relational/RelationshipPulseChecker';
import { RelationalPatternsExplorer } from '@/components/insights/RelationalPatternsExplorer';
import { ChallengesSummaryPanel } from '@/components/challenges/ChallengesSummaryPanel';

export default function Vault() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Derive all view state directly from URL params - URL is the single source of truth
  const tabParam = searchParams.get('tab') || 'library';
  const sectionParam = searchParams.get('section');
  
  // These are derived values, not state - they update automatically when URL changes
  const activeTab = tabParam;
  
  // Sub-sections within the insights tab
  const showEmotionalPatterns = tabParam === 'insights' && sectionParam === 'emotional-patterns';
  const showEnergeticPatterns = tabParam === 'insights' && sectionParam === 'energetic-patterns';
  const showMentalPatterns = tabParam === 'insights' && sectionParam === 'mental-patterns';
  const showRelationalPulse = tabParam === 'insights' && sectionParam === 'relational-pulse';
  const showRelationalPatterns = tabParam === 'insights' && sectionParam === 'relational-patterns';

  // Editable content
  const pageTitle = useEditableContent('vault.page.title', 'The Vault', 'vault');
  const pageDescription = useEditableContent('vault.page.description', 'Your complete personal archive and multi-dimensional insights hub', 'vault');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Simple handlers - just update URL, view state is derived automatically
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const handleOpenEmotionalPatterns = () => {
    setSearchParams({ tab: 'insights', section: 'emotional-patterns' });
  };

  const handleOpenEnergeticPatterns = () => {
    setSearchParams({ tab: 'insights', section: 'energetic-patterns' });
  };

  const handleOpenMentalPatterns = () => {
    setSearchParams({ tab: 'insights', section: 'mental-patterns' });
  };

  const handleOpenRelationalPulse = () => {
    setSearchParams({ tab: 'insights', section: 'relational-pulse' });
  };

  const handleOpenRelationalPatterns = () => {
    setSearchParams({ tab: 'insights', section: 'relational-patterns' });
  };

  const handleBackFromInsights = () => {
    setSearchParams({ tab: 'insights' });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const libraryCategories = [
    {
      id: 'journals',
      title: 'Journal Entries',
      description: 'All your Quantum Journal entries',
      icon: BookOpen,
      color: 'from-primary/20 to-primary/10',
      count: 0 // TODO: fetch actual count
    },
    {
      id: 'recordings',
      title: 'Voice Recordings',
      description: 'Therapy sessions and reflections',
      icon: Mic,
      color: 'from-secondary/20 to-secondary/10',
      count: 0
    },
    {
      id: 'manifestations',
      title: 'Manifestation Movies',
      description: 'Your vision boards and hope reels',
      icon: Film,
      color: 'from-accent/20 to-accent/10',
      count: 0
    },
    {
      id: 'letters',
      title: 'Future Self Letters',
      description: 'Messages to and from your future self',
      icon: Mail,
      color: 'from-[#CBB281]/20 to-[#CBB281]/10',
      count: 0
    },
    {
      id: 'dreams',
      title: 'Dream Journal',
      description: 'Dream logs and interpretations',
      icon: Moon,
      color: 'from-[#4ba2a4]/20 to-[#4ba2a4]/10',
      count: 0
    },
    {
      id: 'oracle',
      title: 'Oracle Readings',
      description: 'Saved card readings and insights',
      icon: Sparkles,
      color: 'from-[#aad8d6]/20 to-[#aad8d6]/10',
      count: 0
    },
    {
      id: 'exercises',
      title: 'Saved Exercises',
      description: 'Sanctuary tools and practices',
      icon: Target,
      color: 'from-primary/20 to-secondary/10',
      count: 0
    },
    {
      id: 'meditations',
      title: 'Meditation History',
      description: 'Completed sessions and favourites',
      icon: Brain,
      color: 'from-[#2F5354]/20 to-[#2F5354]/10',
      count: 0
    }
  ];

  const insightCategories = [
    // Inner Layers - ordered: Emotional → Mental → Energetic → Relational
    {
      id: 'emotional-patterns',
      title: 'Emotional Patterns & Mood',
      description: 'Anxiety, trauma, mood shifts & nervous system correlations',
      icon: HeartPulse,
      color: 'from-rose-500/20 to-purple-500/10',
      action: handleOpenEmotionalPatterns,
      badge: 'Sanctuary'
    },
    {
      id: 'mental-patterns',
      title: 'Mental Patterns & Cognitive Load',
      description: 'Brain fog, rumination, intrusive thoughts & mental fatigue correlations',
      icon: Brain,
      color: 'from-indigo-500/20 to-blue-500/10',
      action: handleOpenMentalPatterns,
      badge: 'Sanctuary'
    },
    {
      id: 'energy',
      title: 'Energetic Patterns & Spiritual Alignment',
      description: 'Vibrational frequency, chakra work & spiritual connection',
      icon: Zap,
      color: 'from-[#4ba2a4]/20 to-[#aad8d6]/10',
      action: handleOpenEnergeticPatterns,
      badge: 'Sanctuary'
    },
    {
      id: 'relational-patterns',
      title: 'Relational Patterns & Connection',
      description: 'See how feeling supported, lonely or connected links with mood over time',
      icon: Heart,
      color: 'from-rose-500/20 to-pink-500/10',
      action: handleOpenRelationalPatterns,
      badge: 'Sanctuary'
    },
    // Other insight categories
    {
      id: 'relational-pulse',
      title: 'Relational Pulse',
      description: 'Connection with yourself, partners, family & community',
      icon: Users,
      color: 'from-pink-500/20 to-rose-500/10',
      action: handleOpenRelationalPulse
    },
    {
      id: 'patterns',
      title: 'Behavioural Patterns',
      description: 'Recurring themes and triggers',
      icon: TrendingUp,
      color: 'from-secondary/20 to-secondary/10'
    },
    {
      id: 'progress',
      title: 'Progress Tracking',
      description: 'Your growth over time',
      icon: Activity,
      color: 'from-accent/20 to-accent/10'
    },
    {
      id: 'cycles',
      title: 'Cycle Insights',
      description: 'Hormonal and lunar pattern analysis',
      icon: Moon,
      color: 'from-[#aad8d6]/20 to-[#4ba2a4]/10'
    },
    {
      id: 'predictions',
      title: 'Predictive Insights',
      description: 'AI-powered wellness forecasting',
      icon: Eye,
      color: 'from-[#CBB281]/20 to-[#CBB281]/10'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-therapeutic">
      <PageHeader title="The Vault" />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Hero Header */}
        <div className="text-center space-y-4 py-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Lock className="h-12 w-12 text-primary" />
            <h1 className="text-5xl md:text-6xl font-light tracking-tight text-foreground animate-fade-in drop-shadow-lg">
              {pageTitle}
            </h1>
          </div>
          <p className="text-lg text-foreground/80 max-w-2xl mx-auto font-light">
            {pageDescription}
          </p>
        </div>

        {/* Search and Export */}
        <Card className="p-4 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search across all your content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Export All
              </Button>
            </div>
          </div>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8">
          <TabsList className="grid w-full grid-cols-3 h-auto gap-3 bg-card/50 backdrop-blur-sm p-2">
            <TabsTrigger
              value="library"
              className="flex flex-col items-center gap-2 py-4 px-4 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary/20 data-[state=active]:to-primary/10"
            >
              <BookOpen className="h-6 w-6" />
              <span className="text-sm font-light">Personal Library</span>
            </TabsTrigger>
            <TabsTrigger
              value="correlations"
              className="flex flex-col items-center gap-2 py-4 px-4 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary/20 data-[state=active]:to-primary/10"
            >
              <Heart className="h-6 w-6" />
              <span className="text-sm font-light">Mind-Body Correlations</span>
            </TabsTrigger>
            <TabsTrigger
              value="insights"
              className="flex flex-col items-center gap-2 py-4 px-4 data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary/20 data-[state=active]:to-primary/10"
            >
              <TrendingUp className="h-6 w-6" />
              <span className="text-sm font-light">Multi-Dimensional Insights</span>
            </TabsTrigger>
          </TabsList>

          {/* Library Tab */}
          <TabsContent value="library" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {libraryCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <Card
                    key={category.id}
                    className="group cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl border-2 hover:border-primary/50 overflow-hidden"
                  >
                    <div className={`h-2 bg-gradient-to-r ${category.color}`} />
                    <CardContent className="p-6 space-y-4">
                      <div className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-br ${category.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <Icon className="h-8 w-8 text-foreground" />
                      </div>
                      <div className="text-center space-y-2">
                        <h3 className="font-light text-base tracking-wide text-foreground">
                          {category.title}
                        </h3>
                        <p className="text-xs text-foreground/70 font-light leading-relaxed">
                          {category.description}
                        </p>
                        <div className="pt-2">
                          <span className="text-2xl font-light text-primary">{category.count}</span>
                          <span className="text-xs text-muted-foreground ml-1">items</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Timeline View Option */}
            <Card className="p-6 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Calendar className="h-8 w-8 text-primary" />
                  <div>
                    <h3 className="text-lg font-light text-foreground">Timeline View</h3>
                    <p className="text-sm text-muted-foreground">See all your content chronologically</p>
                  </div>
                </div>
                <Button>
                  View Timeline
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Correlations Tab - Mind-Body Correlations */}
          <TabsContent value="correlations" className="space-y-6">
            {/* What This Tab Is For */}
            <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
              <CardContent className="p-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Heart className="w-6 h-6 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-light text-foreground">Mind-Body Correlations</h2>
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      This page shows you the <strong>connections between your daily habits and your health</strong>. 
                      When you log things like sleep, food choices, or stress levels, we analyse how they relate to each other over time.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <strong>How to use:</strong> Review the "Detected Patterns" below to see relationships we've found in your data. 
                      Then use the "Interactive Explorer" to dig deeper into specific factors you're curious about.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <CorrelationExplorerComponent />
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-6">
          {showEmotionalPatterns ? (
              /* Emotional Patterns Explorer View */
              <div className="space-y-6">
                {/* Breadcrumb Navigation */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackFromInsights}
                    className="hover:bg-primary/10 hover:text-primary h-auto p-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Multi-Dimensional Insights
                  </Button>
                  <span>/</span>
                  <span className="text-foreground font-medium">Emotional Patterns</span>
                </div>
                
                {/* What This Page Is */}
                <Card className="bg-gradient-to-br from-rose-500/5 to-purple-500/5 border-rose-500/20">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center shrink-0">
                        <HeartPulse className="w-6 h-6 text-rose-500" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-xl font-light text-foreground">Emotional Patterns & Mood</h2>
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          Understand how your <strong>anxiety, trauma responses, and mood shifts</strong> connect to lifestyle factors.
                          See what supports or destabilises your emotional regulation.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <strong>What you'll learn:</strong> Which habits help stabilise your mood, and which may be triggering emotional dysregulation.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <EmotionalPatternsExplorer />
              </div>
            ) : showEnergeticPatterns ? (
              /* Energetic Patterns Explorer View */
              <div className="space-y-6">
                {/* Breadcrumb Navigation */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackFromInsights}
                    className="hover:bg-primary/10 hover:text-primary h-auto p-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Multi-Dimensional Insights
                  </Button>
                  <span>/</span>
                  <span className="text-foreground font-medium">Energetic Patterns</span>
                </div>
                
                {/* What This Page Is */}
                <Card className="bg-gradient-to-br from-[#4ba2a4]/5 to-[#aad8d6]/5 border-[#4ba2a4]/20">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#4ba2a4]/10 flex items-center justify-center shrink-0">
                        <Zap className="w-6 h-6 text-[#4ba2a4]" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-xl font-light text-foreground">Energetic Patterns & Spiritual Alignment</h2>
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          Explore how your <strong>vibrational frequency, chakra work, and spiritual practices</strong> connect to your overall sense of wellbeing.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <strong>What you'll learn:</strong> How your energetic practices correlate with your physical and emotional states over time.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <EnergeticPatternsExplorer />
              </div>
            ) : showRelationalPulse ? (
              /* Relational Pulse Checker View */
              <div className="space-y-6">
                {/* Breadcrumb Navigation */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackFromInsights}
                    className="hover:bg-primary/10 hover:text-primary h-auto p-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Multi-Dimensional Insights
                  </Button>
                  <span>/</span>
                  <span className="text-foreground font-medium">Relational Pulse</span>
                </div>
                
                {/* What This Page Is */}
                <Card className="bg-gradient-to-br from-pink-500/5 to-rose-500/5 border-pink-500/20">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center shrink-0">
                        <Users className="w-6 h-6 text-pink-500" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-xl font-light text-foreground">Relational Pulse Check-In</h2>
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          A compassionate, trauma-informed check-in for your <strong>relational wellbeing</strong>. 
                          Track your sense of connection with yourself, your loved ones, and your wider community.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <strong>How to use:</strong> Complete regular pulse checks to build a picture of your relational health over time.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <RelationshipPulseChecker onBack={handleBackFromInsights} />
              </div>
            ) : showRelationalPatterns ? (
              /* Relational Patterns Explorer View */
              <div className="space-y-6">
                {/* Breadcrumb Navigation */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackFromInsights}
                    className="hover:bg-primary/10 hover:text-primary h-auto p-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Multi-Dimensional Insights
                  </Button>
                  <span>/</span>
                  <span className="text-foreground font-medium">Relational Patterns</span>
                </div>
                
                {/* What This Page Is */}
                <Card className="bg-gradient-to-br from-rose-500/5 to-pink-500/5 border-rose-500/20">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center shrink-0">
                        <Heart className="w-6 h-6 text-rose-500" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-xl font-light text-foreground">Relational Patterns & Connection</h2>
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          See how your <strong>sense of connection, loneliness, and social support</strong> links to your mood and nervous system over time.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <strong>What you'll learn:</strong> How relationships and social connection affect your emotional and physical wellbeing.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <RelationalPatternsExplorer />
              </div>
            ) : showMentalPatterns ? (
              /* Mental Patterns Explorer View */
              <div className="space-y-6">
                {/* Breadcrumb Navigation */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackFromInsights}
                    className="hover:bg-primary/10 hover:text-primary h-auto p-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Multi-Dimensional Insights
                  </Button>
                  <span>/</span>
                  <span className="text-foreground font-medium">Mental Patterns</span>
                </div>
                
                {/* What This Page Is */}
                <Card className="bg-gradient-to-br from-indigo-500/5 to-blue-500/5 border-indigo-500/20">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                        <Brain className="w-6 h-6 text-indigo-500" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-xl font-light text-foreground">Mental Patterns & Cognitive Load</h2>
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          Explore your <strong>thinking patterns - brain fog, rumination, intrusive thoughts, and mental fatigue</strong>.
                          These are information about what your nervous system needs, not verdicts.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <strong>What you'll learn:</strong> Which factors correlate with mental clarity vs cognitive overload for you personally.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <MentalPatternsExplorer />
              </div>
            ) : (
              /* Insights Categories Grid */
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {insightCategories.map((category) => {
                    const Icon = category.icon;
                    const categoryWithBadge = category as typeof category & { badge?: string };
                    return (
                      <Card
                        key={category.id}
                        className="group cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl border-2 hover:border-primary/50 overflow-hidden"
                        onClick={() => category.action ? category.action() : null}
                      >
                        <div className={`h-2 bg-gradient-to-r ${category.color}`} />
                        <CardContent className="p-6 space-y-4">
                          <div className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-br ${category.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                            <Icon className="h-8 w-8 text-foreground" />
                          </div>
                          <div className="text-center space-y-2">
                            <div className="flex items-center justify-center gap-2">
                              <h3 className="font-light text-base tracking-wide text-foreground">
                                {category.title}
                              </h3>
                              {categoryWithBadge.badge && (
                                <span className="px-2 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded-full">
                                  {categoryWithBadge.badge}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-foreground/70 font-light leading-relaxed">
                              {category.description}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* AI Insights Generator */}
                <Card className="p-6 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                        <Brain className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <div>
                        <h3 className="text-lg font-light text-foreground">AI Journey Reflection</h3>
                        <p className="text-sm text-muted-foreground">Generate personalised insights from your entire journey</p>
                      </div>
                    </div>
                    <Button className="w-full" size="lg">
                      Generate Reflection
                    </Button>
                  </div>
                </Card>

                {/* Challenges & Commitments */}
                <section className="space-y-3">
                  <div>
                    <h2 className="text-lg font-light text-foreground">
                      Challenges & Commitments
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      A quick view of your personal and community challenges. Use this alongside the Mind-Body Correlations to see how your choices ripple through your wellbeing.
                    </p>
                  </div>
                  <ChallengesSummaryPanel />
                </section>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
